import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { useTranslate } from '@tolgee/react';
import { find, reduce, map, uniqBy, forEach } from 'lodash-es';
import { useResultTypeLabel } from 'views/Todo/hooks/useLabel';
import { officialResultTypes } from 'views/Aide/Basis/Todo/TodoType';
import type { TypeSource } from './NewTodo';

/** useResultForm 入参，由调用方从 Context 或 props 获取后传入，实现与 Context 解耦 */
export type UseResultFormParams = {
  typeSource: TypeSource;
  readonly: boolean;
};

export type ResultOption = { label: string; value: string };

const SPECIAL_TYPE_NO_OPTIONS = -998;
const TODO_STATUS_COMPLETED = 3;

/** 从 policy 的 templates 构建可选结果列表（纯函数，便于单测） */
export function buildResultOptionsFromPolicy(
  policy: TypeSource[number] | undefined,
  getResultTypeLabel: (value: string) => string,
): ResultOption[] {
  if (!policy?.origin?.templates?.length) return [];

  const templates = policy.origin.templates;
  const resultTypes = map(uniqBy(templates, 'resultType'), 'resultType');
  const newTemplates: Todo.PolicyTemplate[] = [];

  forEach(resultTypes, (resultType) => {
    const enabled = find(templates, (e) => e.resultType === resultType && e.enable);
    const disabled = find(templates, (e) => e.resultType === resultType && !e.enable);
    newTemplates.push(enabled ?? disabled!);
  });

  return reduce(
    newTemplates,
    (acc, cur) => {
      if (!cur.enable) return acc;
      if (cur.parentResultType) {
        const exists = find(acc, (e) => e.value === cur.parentResultType);
        if (!exists) {
          acc.push({
            label: getResultTypeLabel(cur.parentResultType),
            value: cur.parentResultType,
          });
        }
      } else {
        acc.push({
          label: getResultTypeLabel(cur.resultType),
          value: cur.resultType,
        });
      }
      return acc;
    },
    [] as ResultOption[],
  );
}

/** 计算当前选中结果的展示文案（只读态用，纯函数） */
export function getResultDisplayText(
  operateResult: string,
  customOperateResult: string,
  options: ResultOption[],
  todoStatus: number,
  getResultTypeLabel: (value: string) => string,
  t: (key: string) => string,
): string {
  const fallback =
    operateResult === 'OTHER'
      ? customOperateResult || t('resultFallback.OTHER')
      : options.find((e) => e.value === operateResult)?.label ??
        operateResult ??
        t('todo_result_no_select');

  if (todoStatus === TODO_STATUS_COMPLETED) {
    return officialResultTypes.includes(operateResult)
      ? getResultTypeLabel(operateResult)
      : fallback;
  }
  return fallback;
}

export function isNoOptions(todo: Todo.TodoInstance) {
  const { todoStatus, type } = todo;
  return todoStatus === TODO_STATUS_COMPLETED || type === SPECIAL_TYPE_NO_OPTIONS;
}

export function isTodoCompleted(todoStatus: Todo.Status) {
  return todoStatus === TODO_STATUS_COMPLETED;
}

/** 是否是待认领状态 */
export function isTodoUnclaimed(todoStatus: Todo.Status) {
  return todoStatus === 1;
}

/**
 * Result 组件的业务 hook：结果选项、展示文案、只读/禁用、选择回调。
 * 不依赖 TypeSourceContext / ReadonlyContext，由调用方通过 params 传入，便于复用与单测。
 */
export function useResultForm(params: UseResultFormParams) {
  const { typeSource, readonly } = params;
  const { t } = useTranslate();
  const { values, errors, dirty, setValues, handleSubmit } = useFormikContext<Todo.TodoInstance>();
  const { getResultTypeLabel } = useResultTypeLabel();

  const { type, todoStatus, operateResult, customOperateResult, id } = values;

  const resultOptions = useMemo(() => {
    if (isNoOptions(values)) return [];
    const policy = typeSource?.find((e) => e.type === type);
    return buildResultOptionsFromPolicy(policy, getResultTypeLabel);
  }, [typeSource, type, getResultTypeLabel, values]);

  const displayText = useMemo(
    () =>
      getResultDisplayText(
        operateResult,
        customOperateResult,
        resultOptions,
        todoStatus,
        getResultTypeLabel,
        t,
      ),
    [operateResult, customOperateResult, resultOptions, todoStatus, getResultTypeLabel, t],
  );

  const isCompleted = isTodoCompleted(todoStatus);
  const isReadonly = readonly || isCompleted;
  const isOtherSelected = operateResult === 'OTHER';

  const onChecked = (value: string) => {
    setValues({ ...values, operateResult: value });
    handleSubmit();
  };

  const onCustomResultChange = (value: string) => {
    setValues({ ...values, customOperateResult: value });
  };

  const onCustomResultBlur = () => {
    if (dirty) handleSubmit();
  };

  return {
    resultOptions,
    displayText,
    isReadonly,
    isCompleted,
    isOtherSelected,
    errors,
    disabled: isReadonly,
    values: { id, operateResult, customOperateResult },
    onChecked,
    onCustomResultChange,
    onCustomResultBlur,
  };
}
